import React, { Fragment, useEffect, useState } from 'react';

import { Dialog, Transition } from '@headlessui/react';
import { useAtom } from 'jotai';
import Image from 'next/image';
import Link from 'next/link';
import { CgClose } from 'react-icons/cg';
import { GiPayMoney, GiReceiveMoney } from 'react-icons/gi';

import { getCategory } from '@/api/fetch/marketplace-api';
import {
  CategoryByIDRequest,
  CategoryResponse,
} from '@/api/gen-protos/categories_pb';
import { categories } from '@/lib/helpers';
import { AuthTokenAtom } from '@/modules/core/store/grpc-free-store';
import { IListingItem } from '@/modules/core/types';

interface Props {
  title: string;
  buttonElement: JSX.Element;
  adListData: IListingItem[];
  loadMoreAds: () => void;
  showSelction: boolean;
  setShowSelction: (s: boolean) => void;
  setShowAdList: (s: boolean) => void;
  balance: number;
}

const ClassicBoostPlan = ({
  title,
  buttonElement,
  setShowSelction,
  setShowAdList,
  balance,
  adListData,
}: Props) => {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [selectedAds, setSelectedAds] = useState<string[]>([]);
  const [timestamp, setTimestamp] = useState(0);
  const [categoryPrices, setCategoryPrices] = useState<Record<string, string>>(
    {}
  );
  const [total, setTotal] = useState('0');
  const [authToken] = useAtom(AuthTokenAtom);
  const [boostSuccess, setBoostSuccess] = useState(false);
  function closeModal() {
    setModalIsOpen(false);
  }
  function openModal() {
    setModalIsOpen(true);
  }

  const getCategoryPrice = async (categoryName: string | undefined) => {
    if (categoryName === undefined) {
      return '';
    }
    let categoryData: CategoryResponse = new CategoryResponse();
    const categoryByIDRequest = new CategoryByIDRequest();
    const cat = categories.find((category) => category.name === categoryName);
    categoryByIDRequest.setId(cat?.id || '');
    categoryData = await getCategory(categoryByIDRequest);
    return (
      categoryData
        ?.getCategory()
        ?.getProductpriceMap()
        ?.get('boost')
        ?.toString() || ''
    );
  };

  useEffect(() => {
    const fetchCategoryPricesForAllAds = async () => {
      const prices: Record<string, string> = {};

      for (const adCardData of adListData) {
        if (adCardData.metadata?.category && adCardData.metadata.state === 2) {
          const categoryName = adCardData.metadata.category;
          const price = await getCategoryPrice(categoryName);
          prices[categoryName] = price || '';
        }
      }

      setCategoryPrices(prices);
    };

    fetchCategoryPricesForAllAds();
  }, [adListData]);

  const handleTimestampChange = (e: any) => {
    const selectedTimestamp = e.target.value;
    const timestampInSeconds = Date.parse(selectedTimestamp) / 1000;
    setTimestamp(timestampInSeconds);
  };

  const handleConfirm = async () => {
    if (timestamp !== null && selectedAds?.length >= 1) {
      try {
        const response = await fetch('/api/boost', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            selectedAds,
            timestamp,
            authToken,
            type: 'planned',
          }),
        });

        if (response.ok) {
          setBoostSuccess(true);
        } else {
          console.error('Boost request failed');
        }
      } catch (error) {
        console.error('Error sending boost request:', error);
      }
    }
  };

  const handleCancel = () => {
    setShowSelction(false);
    setShowAdList(true);
    closeModal();
    setTimestamp(0);
    setSelectedAds([]);
    setTotal('0');
    setBoostSuccess(false);
  };

  const toggleAdSelection = (adId: string, price: string) => {
    if (selectedAds.includes(adId)) {
      setSelectedAds(selectedAds.filter((id) => id !== adId));
      setTotal((prevTotal) =>
        (parseFloat(prevTotal) - parseFloat(price)).toString()
      );
    } else {
      setSelectedAds([...selectedAds, adId]);
      setTotal((prevTotal) =>
        (parseFloat(prevTotal) + parseFloat(price)).toString()
      );
    }
  };
  return (
    <>
      {React.cloneElement(buttonElement, {
        onClick: () => {
          openModal();
        },
      })}
      <Transition appear show={modalIsOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={closeModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-white bg-opacity-60 backdrop-blur-md" />
          </Transition.Child>

          <div className="w-full h-full fixed top-16  md:right-0 overflow-y-auto overflow-x-scroll">
            <div className="flex w-full h-full items-center justify-center text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel
                  className="!p-4 transform overflow-hidden \
                             rounded-2xl border border-secondary/50 bg-white  text-left align-middle \
                             shadow-xl transition-all "
                >
                  <div
                    className="btn btn-sm btn-circle absolute right-4 top-4"
                    onClick={closeModal}
                  >
                    <CgClose size={18} />
                  </div>
                  <Dialog.Title
                    as="h3"
                    className="text-lg md:text-xl font-semibold mt-1"
                  >
                    {title}
                  </Dialog.Title>
                  <div className="flex flex-wrap gap-6 justify-center">
                    <div
                      className={`w-full h-[25rem]  md:pb-10 ${
                        !boostSuccess ? '' : 'hidden'
                      }`}
                    >
                      <h2 className="text-gray-600 text-sm md:text-base mb-4">
                        Choisissez la date et sélectionnez les annonces à
                        planifier pour les booster.
                      </h2>
                      <input
                        type="datetime-local"
                        onChange={(e) => handleTimestampChange(e)}
                        className="w-full p-2 my-4 border border-gray-300 rounded-md focus:ring focus:ring-blue-200"
                      />
                      <div className="overflow-x-auto max-h-[180px] md:max-h-[210px] overflow-y-scroll md:overflow-y-auto">
                        <table className="min-w-full mt-2 border border-gray-300 rounded-md">
                          <thead>
                            <tr className="bg-gray-50">
                              <th className="py-2 px-3 border-b truncate text-center"></th>
                              <th className="py-2 px-3 border-b truncate text-center">
                                Image
                              </th>
                              <th className="py-2 px-3 border-b truncate text-start">
                                Titre
                              </th>
                              <th className="py-2 px-3 border-b truncate text-center">
                                Categorie
                              </th>
                              <th className="py-2 px-3 border-b truncate text-center">
                                Ville / Délégation
                              </th>
                              <th className="py-2 px-3 border-b truncate text-center">
                                Prix
                              </th>
                              <th className="py-2 px-3 border-b truncate text-center">
                                Prix de boost
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {adListData
                              ?.filter((ad) => ad?.metadata?.state === 2)
                              .map((adCardData) => (
                                <tr key={adCardData.id}>
                                  <td className="py-2 px-3 border-b border-gray-300">
                                    <input
                                      type="checkbox"
                                      onChange={() =>
                                        toggleAdSelection(
                                          adCardData.id,
                                          categoryPrices[
                                            adCardData?.metadata?.category || ''
                                          ] || '0'
                                        )
                                      }
                                      checked={selectedAds.includes(
                                        adCardData.id
                                      )}
                                    />
                                  </td>
                                  <td className="py-2 px-3 border-b border-gray-300">
                                    <img
                                      src={adCardData.images[0]}
                                      width={50}
                                      height={50}
                                      alt={adCardData.title}
                                      className={'rounded'}
                                    />
                                  </td>
                                  <td className="py-2 px-3 border-b border-gray-300 truncate">
                                    {adCardData.title}
                                  </td>
                                  <td className="py-2 px-3 border-b border-gray-300 truncate text-center">
                                    {adCardData?.metadata?.category}
                                  </td>
                                  <td className="py-2 px-3 border-b border-gray-300 truncate text-center">
                                    {adCardData?.location?.governorate} -{' '}
                                    {adCardData?.location?.delegation}
                                  </td>
                                  <td className="py-2 px-3 border-b border-gray-300 truncate text-center">
                                    {adCardData.price}
                                  </td>

                                  <td className="py-2 px-3 border-b border-gray-300 truncate text-center">
                                    {categoryPrices[
                                      adCardData?.metadata?.category || ''
                                    ] !== undefined ? (
                                      categoryPrices[
                                        adCardData?.metadata?.category || ''
                                      ]
                                    ) : (
                                      <div
                                        className="spinner-border text-primary"
                                        role="status"
                                      >
                                        <span className="sr-only">
                                          Loading...
                                        </span>
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="w-full h-fit justify-between items-start md:items-center flex gap-x-2  md:gap-x-3 my-4 md:overflow-auto overflow-scroll">
                        <div className="flex w-full h-full ">
                          <div className="w-full h-full flex flex-col justify-between items-start gap-y-2 text-xs md:text-base font-semibold text-gray-800/70 my-2">
                            <span className="flex truncate text-xs md:text-base items-center gap-2">
                              <GiReceiveMoney
                                className="text-green-500"
                                size={22}
                              />
                              Total de jetons: <b>{balance}</b>
                            </span>
                            {total !== '0' && (
                              <div className="flex w-full">
                                <div className="w-full flex justify-between text-lg font-semibold text-gray-800/70 ">
                                  {balance >= parseFloat(total) ? (
                                    <span className="flex truncate text-xs md:text-base items-center gap-x-2">
                                      <GiPayMoney
                                        className="text-primary"
                                        size={22}
                                      />
                                      Total de dépenses : <b> {total}</b>
                                    </span>
                                  ) : (
                                    <span className="mx-3 text-xl text-danger">
                                      Vous avez besoin de plus de jetons.{' '}
                                      <Link
                                        href="/buy-tokens"
                                        className="text-blue-600 hover:underline"
                                      >
                                        Achetez-les ici
                                      </Link>
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="w-full flex flex-wrap gap-3 justify-end">
                          <button
                            hidden={
                              selectedAds.length < 1 ||
                              timestamp === 0 ||
                              balance < parseFloat(total)
                            }
                            onClick={() => handleConfirm()}
                            className="w-full md:w-fit truncate text-xs md:text-sm bg-gradient-to-tr from-yellow-300 via-orange-400 to-orange-300 text-white hover:from-yellow-500 hover:to-orange-500 group shadow-sm  hover:shadow-none font-semibold rounded-lg  px-4 py-2"
                          >
                            Planifier le boost
                          </button>
                          <button
                            onClick={() => handleCancel()}
                            className="w-full md:w-fit truncate text-xs md:text-sm font-semibold rounded-lg md:border-2 bg-gray-200 px-4 py-2 hover:bg-gray-300"
                          >
                            Annuler
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className={`${boostSuccess ? 'block' : 'hidden'}`}>
                      <div className="flex flex-col gap-5 items-center justify-center mt-4">
                        <Image
                          width={176}
                          height={157}
                          src="/media/t-celebrate.svg"
                          alt="tayara illustration : celebrate"
                          className="w-40"
                        />
                        <h3 className="text-sm lg:text-xl font-base italic text-center mx-8 mb-4">
                          Boost planifiée avec succès
                        </h3>
                        <button
                          onClick={() => handleCancel()}
                          className="truncate text-sm font-semibold rounded-lg md:border-2 bg-gray-200 px-4 py-2 hover:bg-gray-300"
                        >
                          Fermer
                        </button>
                      </div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default ClassicBoostPlan;
